// ════════════════════════════════════════════════════════════════════════════
// 📸 CameraScanner — Cross-browser barcode/QR scanner
// Uses html5-qrcode (ZXing-based) — works on ALL browsers incl. iOS Safari 15+,
// iOS Chrome, Firefox, etc. No dependency on native BarcodeDetector API.
// ════════════════════════════════════════════════════════════════════════════
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Camera, Zap, AlertTriangle, Keyboard, Flashlight, FlashlightOff } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';

interface CameraScannerProps {
  onDetected: (barcode: string) => void;
  onClose: () => void;
}

export function CameraScanner({ onDetected, onClose }: CameraScannerProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [detected, setDetected] = useState('');
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const manualRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);

  // Toggle torch/flashlight
  const toggleTorch = useCallback(async () => {
    if (!trackRef.current) return;
    try {
      const capabilities = trackRef.current.getCapabilities() as any;
      if (!capabilities.torch) {
        setTorchSupported(false);
        return;
      }
      const newState = !torchOn;
      await trackRef.current.applyConstraints({
        advanced: [{ torch: newState } as any]
      });
      setTorchOn(newState);
    } catch (err) {
      console.warn('Torch toggle failed:', err);
      setTorchSupported(false);
    }
  }, [torchOn]);

  useEffect(() => {
    mountedRef.current = true;
    startScanner();
    return () => {
      mountedRef.current = false;
      stopScanner();
    };
  }, []);

  const stopScanner = async () => {
    try {
      if (scannerRef.current?.isScanning) {
        await scannerRef.current.stop();
      }
      scannerRef.current?.clear();
    } catch {}
    // Turn off torch if it was on
    if (trackRef.current && torchOn) {
      try {
        await trackRef.current.applyConstraints({
          advanced: [{ torch: false } as any]
        });
      } catch {}
    }
  };

  const startScanner = async () => {
    try {
      const containerId = 'html5qr-scanner-region';
      // Wait for DOM element to render
      await new Promise(r => setTimeout(r, 150));
      if (!mountedRef.current) return;

      const scanner = new Html5Qrcode(containerId);
      scannerRef.current = scanner;

      // Get camera stream for torch support
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        }
      });

      // Get the video track for torch control
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        trackRef.current = videoTrack;
        // Check if torch is supported
        try {
          const capabilities = videoTrack.getCapabilities() as any;
          if (capabilities.torch) {
            setTorchSupported(true);
          }
        } catch {}
      }

      // Stop the stream — html5-qrcode will start its own
      stream.getTracks().forEach(t => t.stop());

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 15, // Faster scanning
          qrbox: { width: 320, height: 200 }, // Larger scan area
          aspectRatio: 1.5,
          disableFlip: false,
          videoConstraints: {
            facingMode: 'environment',
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        },
        (decodedText) => {
          if (!mountedRef.current) return;
          setDetected(decodedText);
          // Get video track from the scanner's stream for torch
          try {
            const videoEl = document.querySelector('#html5qr-scanner-region video') as HTMLVideoElement;
            if (videoEl?.srcObject) {
              const tracks = (videoEl.srcObject as MediaStream).getVideoTracks();
              if (tracks[0]) {
                trackRef.current = tracks[0];
                const caps = tracks[0].getCapabilities() as any;
                if (caps.torch) setTorchSupported(true);
              }
            }
          } catch {}
          stopScanner();
          setTimeout(() => {
            if (mountedRef.current) {
              onDetected(decodedText);
              onClose();
            }
          }, 400);
        },
        // Scan failure callback (no barcode found in this frame — expected)
        () => {}
      );

      // After scanner starts, get the video track from the actual stream
      try {
        const videoEl = document.querySelector('#html5qr-scanner-region video') as HTMLVideoElement;
        if (videoEl?.srcObject) {
          const tracks = (videoEl.srcObject as MediaStream).getVideoTracks();
          if (tracks[0]) {
            trackRef.current = tracks[0];
            const caps = tracks[0].getCapabilities() as any;
            if (caps.torch) {
              setTorchSupported(true);
            }
          }
        }
      } catch {}

      if (mountedRef.current) setScanning(true);
    } catch (err: any) {
      if (!mountedRef.current) return;
      const msg = typeof err === 'string' ? err : err?.message || 'Unknown error';
      if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
        setCameraError('Camera access denied. Grant permission or use manual entry.');
      } else if (msg.includes('NotFoundError') || msg.includes('No camera')) {
        setCameraError('No camera found. Use manual entry below.');
      } else {
        setCameraError(`Camera error: ${msg}. Use manual entry below.`);
      }
    }
  };

  const handleManual = (e: React.FormEvent) => {
    e.preventDefault();
    const val = manualRef.current?.value.trim();
    if (val) { onDetected(val); onClose(); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10002] bg-black/92 backdrop-blur-md flex flex-col items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="w-full max-w-sm bg-[#080b18] border border-white/10 rounded-3xl overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.07]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
              <Camera size={13} className="text-cyan-400" />
            </div>
            <div>
              <p className="text-[12px] font-black text-white">Barcode / QR Scanner</p>
              <p className="text-[9px] text-slate-600 font-mono">Point camera at barcode</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Torch/Flashlight button */}
            {torchSupported && (
              <button
                onClick={toggleTorch}
                className={`p-2 rounded-lg transition-all ${
                  torchOn
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                    : 'bg-white/5 text-slate-500 hover:text-white hover:bg-white/10 border border-transparent'
                }`}
                title={torchOn ? 'Turn off flashlight' : 'Turn on flashlight'}
              >
                {torchOn ? <Flashlight size={16} /> : <FlashlightOff size={16} />}
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/8 text-slate-600 hover:text-white transition-colors">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Camera view — html5-qrcode renders into this div */}
        {!cameraError ? (
          <div className="relative bg-black w-full" style={{ minHeight: 320 }}>
            <div
              id="html5qr-scanner-region"
              ref={containerRef}
              className="w-full h-full"
              style={{ minHeight: 320 }}
            />

            {/* Scan guide overlay */}
            <div className="absolute inset-0 pointer-events-none z-5">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-[85%] h-[55%] border-2 border-cyan-400/30 rounded-xl">
                  {/* Corner markers */}
                  <div className="absolute -top-0.5 -left-0.5 w-5 h-5 border-t-2 border-l-2 border-cyan-400 rounded-tl-lg" />
                  <div className="absolute -top-0.5 -right-0.5 w-5 h-5 border-t-2 border-r-2 border-cyan-400 rounded-tr-lg" />
                  <div className="absolute -bottom-0.5 -left-0.5 w-5 h-5 border-b-2 border-l-2 border-cyan-400 rounded-bl-lg" />
                  <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 border-b-2 border-r-2 border-cyan-400 rounded-br-lg" />
                </div>
              </div>
            </div>

            {/* Status overlay */}
            <AnimatePresence>
              {detected ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute inset-0 flex items-center justify-center bg-emerald-500/20 backdrop-blur-sm z-10"
                >
                  <div className="text-center">
                    <div className="text-3xl mb-2">✓</div>
                    <p className="text-emerald-400 font-black text-sm">Detected!</p>
                    <p className="text-white font-mono text-xs mt-1">{detected}</p>
                  </div>
                </motion.div>
              ) : scanning ? (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-white/10 text-[10px] text-cyan-400 font-mono z-10">
                  <Zap size={9} className="animate-pulse" /> Scanning…
                  {torchSupported && (
                    <span className="ml-1 text-amber-400/60">• Tap 💡 for light</span>
                  )}
                </div>
              ) : null}
            </AnimatePresence>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 px-6 gap-3 text-center">
            <AlertTriangle size={28} className="text-amber-400" />
            <p className="text-[11px] text-amber-300 font-mono">{cameraError}</p>
          </div>
        )}

        {/* Manual entry */}
        <div className="p-5 space-y-3 border-t border-white/[0.06]">
          <div className="flex items-center gap-2 text-[9px] text-slate-600 font-mono uppercase tracking-widest">
            <Keyboard size={10} /> Manual / USB scanner entry
          </div>
          <form onSubmit={handleManual} className="flex gap-2">
            <input
              ref={manualRef}
              autoFocus={!!cameraError}
              placeholder="Scan or type barcode…"
              className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-700 outline-none focus:border-cyan-500/40 font-mono"
            />
            <button
              type="submit"
              className="px-4 py-2.5 rounded-xl bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 text-[11px] font-bold hover:bg-cyan-500/25 transition-all"
            >
              Add
            </button>
          </form>
          <p className="text-[9px] text-slate-700 font-mono text-center">
            USB barcode readers auto-submit • Works on all browsers & devices
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
